import { useState } from 'react';
import { Box, Typography, Avatar, Tooltip, IconButton, Popover } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDestrierTheme } from '../ThemeContext';

const settingsIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

export default function AnalysisHeader({ username, knightTargetRef, profile, onSignOut }) {
  const { c } = useDestrierTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);


  const profileIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
  const settingsMenuIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
  const logoutMenuIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );

  const menuItems = [
    { label: 'Profile', icon: profileIcon, onClick: () => { setAnchorEl(null); navigate('/dashboard/settings'); } },
    { label: 'Settings', icon: settingsMenuIcon, onClick: () => { setAnchorEl(null); navigate('/dashboard/settings'); } },
    { label: 'Sign out', icon: logoutMenuIcon, onClick: () => { setAnchorEl(null); onSignOut?.(); }, danger: true },
  ];

  return (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: { xs: 1.5, md: 3 }, py: { xs: 1, md: 1.5 }, minHeight: { xs: 50, md: 60 },
      background: `${c.pageBg || c.bg}ee`,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: '1px solid #ffffff0a',
    }}>
      {/* Left: Logo */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flexShrink: 0 }}
        onClick={() => navigate('/')}
      >
        <img
          src={c.mode === 'dark' ? '/logo-light.svg' : '/logo.svg'}
          alt="Destrier"
          style={{ width: 32, height: 32, marginBottom: 2 }}
          ref={knightTargetRef}
        />
        <span style={{
          fontFamily: "'Urbanist', sans-serif",
          fontWeight: 700,
          fontSize: 'clamp(1.15rem, 3.5vw, 1.44rem)',
          color: c.textDark,
          letterSpacing: '-0.02em',
        }}>
          Destrier<span style={{ color: c.accent }}>.ai</span>
        </span>
      </Box>

      {/* Dashboard indicator */}
      {location.pathname.startsWith('/dashboard') && (
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 2, color: c.textMuted }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 3a3 3 0 00-3 3v1H9V6a3 3 0 00-6 0v7c0 5 3.5 8 9 8s9-3 9-8V6a3 3 0 00-3-3z" />
          </svg>
          <Typography sx={{ fontSize: 'clamp(1.15rem, 3.5vw, 1.44rem)', fontWeight: 700, color: c.textDark, letterSpacing: '-0.02em' }}>
            Dashboard
          </Typography>
        </Box>
      )}

      {/* Right: Settings + User profile dropdown + Sign out */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
        <Tooltip title="Settings" arrow>
          <IconButton
            onClick={() => location.pathname === '/dashboard/settings' ? navigate(-1) : navigate('/dashboard/settings')}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              width: 36, height: 36, borderRadius: '10px',
              color: c.textMuted,
              '&:hover': { color: c.textDark, backgroundColor: `${c.accent}10` },
            }}
          >
            {settingsIcon}
          </IconButton>
        </Tooltip>

        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: 'pointer', borderRadius: '12px', px: 1, py: 0.5,
            '&:hover': { backgroundColor: `${c.accent}08` },
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: c.textDark, display: { xs: 'none', sm: 'block' } }}>
            {profile?.display_name || username}
          </Typography>
          <Avatar
            sx={{
              width: { xs: 28, md: 32 }, height: { xs: 28, md: 32 },
              backgroundColor: c.accent,
              fontSize: '0.8rem', fontWeight: 700,
            }}
          >
            {(profile?.display_name || username || '?')[0].toUpperCase()}
          </Avatar>
        </Box>

        {onSignOut && (
          <Tooltip title="Sign out" arrow>
            <IconButton
              onClick={onSignOut}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: 36, height: 36, borderRadius: '10px',
                color: c.textMuted,
                '&:hover': { color: '#E4446E', backgroundColor: 'rgba(227,82,120,0.1)' },
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </IconButton>
          </Tooltip>
        )}

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: '12px',
                backgroundColor: c.mode === 'dark' ? '#252530' : '#fff',
                border: `1px solid ${c.cardBorder}`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                minWidth: 180,
                overflow: 'hidden',
              },
            },
          }}
        >
          {/* User info at top */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${c.cardBorder}` }}>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: c.textDark }}>
              {profile?.display_name || 'User'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: c.textMuted }}>
              {username}
            </Typography>
          </Box>

          {/* Menu items */}
          {menuItems.map((item) => (
            <Box
              key={item.label}
              onClick={item.onClick}
              sx={{
                px: 2, py: 1.2,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 1.5,
                fontSize: '0.85rem', fontWeight: 500,
                color: item.danger ? '#E4446E' : c.textDark,
                '&:hover': { backgroundColor: item.danger ? 'rgba(227,82,120,0.08)' : `${c.accent}08` },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{item.icon}</Box>
              {item.label}
            </Box>
          ))}
        </Popover>
      </Box>
    </Box>
  );
}
